import dayjs from 'dayjs';
import { formatAmountInWords } from '@/utils/amountInWords';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE, formatDate as formatDateCanonical } from '@/utils/format';
import {
  formatRentalCharges,
  formatRentalDatesOnly,
  formatRentalLineCommercialBreakdown,
  isRentalQuotationItem,
  isRentalSpecificationsRedundant,
  rentalLineRate,
  rentalPrintNeedsBreakdown,
  resolveQuotationLineNetAmount,
  resolveQuotationLineVatAmount,
  resolveQuotationPrintContext,
} from '@/utils/quotationRentalDisplay';
import type { RevenueServiceTypeOption } from '@/utils/revenueServiceTypeRouting';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import type {
  Quotation,
  QuotationItem,
  QuotationOverhead,
  CustomerOverheadPrintOption,
  QuotationPrintMode,
} from '@/services/quotation.service';
import { splitDescriptionChunks } from './description-chunks';
import type { QuotationPrintItemBase, QuotationPrintModel } from './types';

/** Manufacturing always prints the code column. Other quotations print it when any line has a code. */
export function quotationPrintShowsItemCode(
  model: Pick<QuotationPrintModel, 'manufacturingColumns' | 'items'>
): boolean {
  if (model.manufacturingColumns) return true;
  return model.items.some((item) => item.itemCode.trim().length > 0);
}
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';
import {
  buildRichTextClauseChunks,
  buildRichTextPrintChunks,
  richTextToPlain,
  richTextToPlainLine,
} from '@/utils/richText';
import {
  CONTINUATION_CHROME_HEIGHT_PX,
  getUsableContentHeightPx,
  LETTER_LETTERHEAD_TEMPLATE,
  LINE_HEIGHT_PX,
} from '@/print-engine/page-template';

/** @deprecated Prefer clause chunks + page planner packing for T&C. */
export function getQuotationTermsAndConditionsPageLines(): number {
  const usable = getUsableContentHeightPx(LETTER_LETTERHEAD_TEMPLATE);
  const reserved = CONTINUATION_CHROME_HEIGHT_PX + 18 /* page number */ + 28 /* heading */;
  return Math.max(36, Math.floor((usable - reserved) / LINE_HEIGHT_PX));
}

/** Full-width notes body. Letter content is ~110 characters; commercial terms stay in the narrow column. */
export const QUOTATION_NOTES_CHARS_PER_LINE = 110;
/** Continuation page can hold about this many dense note lines under the letterhead. */
export const QUOTATION_NOTES_MAX_LINES = 42;

/** Join notes + special instructions and split for print pagination. */
export function buildQuotationNoteChunks(notes: string, specialInstructions?: string): string[] {
  const noteOpts = {
    maxLines: QUOTATION_NOTES_MAX_LINES,
    charsPerLine: QUOTATION_NOTES_CHARS_PER_LINE,
  };
  const parts = [notes, specialInstructions]
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  if (!parts.length) return [];
  if (parts.length === 1) {
    return buildRichTextPrintChunks(parts[0], noteOpts);
  }
  const noteChunks = buildRichTextPrintChunks(parts[0], noteOpts);
  const siChunks = buildRichTextPrintChunks(parts[1], noteOpts);
  if (!siChunks.length) return noteChunks;
  return [
    ...noteChunks,
    ...siChunks.map((c, i) => (i === 0 ? `<p><strong>Special Instructions</strong></p>${c}` : c)),
  ];
}

/**
 * Split T&C into clause-sized chunks. Page planner packs them into full pages —
 * do not pre-bundle into page-height blobs (that under-filled pages).
 */
export function buildQuotationTermsAndConditionsChunks(termsAndConditions: string): string[] {
  return buildRichTextClauseChunks(termsAndConditions, { charsPerLine: 110 });
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
}

/** Print amount only (no currency) — preserves prior 2dp locale number formatting. */
function formatAmount(value: unknown): string {
  return formatMoney(value, PRINT_PLAIN_AMOUNT_PROFILE);
}

function formatDate(value?: string): string {
  if (!value) return '';
  // Prefer date-only safe path; fall back to dayjs for non-ISO legacy strings (same DD-MM-YYYY).
  const safe = formatDateCanonical(value, { style: 'print_dmY' });
  if (safe) return safe;
  return dayjs(value).format('DD-MM-YYYY');
}

export type BuildQuotationPrintModelInput = {
  quotation: Quotation & {
    overheads?: QuotationOverhead[];
    profitability_snapshot?: {
      total_overhead?: number;
      loaded_total_cost?: number;
      gross_profit?: number;
      gross_profit_percentage?: number;
    } | null;
  };
  printMode?: QuotationPrintMode;
  customerOverheadDisplay?: CustomerOverheadPrintOption;
  revenueServiceTypeLabel?: string;
  revenueServiceType?: RevenueServiceTypeOption | null;
  approvalSigners?: ApprovalPrintSigner[];
  companyInfo?: {
    taxNumber?: string;
    vatNumber?: string;
  };
  /** Company quotation defaults; blank values keep the built-in wording. */
  coverLetter?: {
    salutation?: string;
    intro?: string;
    introRental?: string;
  };
};

const DEFAULT_INTRO_GREETING = 'Dear Sir / Madam,';

function mapTradingItem(
  item: QuotationItem,
  opts: { internalMode: boolean; technicalMode: boolean }
): QuotationPrintItemBase {
  const description = String(item.item_description || '');
  const specs = String(item.specifications || '').trim();
  const notes = String(item.notes || '').trim();
  const deliveryDays = toNumber(item.delivery_days);
  return {
    id: item.id,
    lineNumber: item.line_number,
    kind: 'trading',
    itemCode: item.item_code?.trim() || '',
    revision: item.revision?.trim() || '',
    description,
    descriptionChunks: splitDescriptionChunks(description),
    specifications: specs,
    notes,
    quantityLabel: `${formatAmount(item.quantity).replace(/\.00$/, '')}${item.uom ? ` ${item.uom}` : ''}`,
    unitPrice: opts.technicalMode ? 0 : toNumber(item.unit_price),
    netAmount: opts.technicalMode ? 0 : resolveQuotationLineNetAmount(item),
    baseCost: opts.internalMode ? toNumber(item.base_cost_amount) : undefined,
    allocatedOh: opts.internalMode ? toNumber(item.allocated_overhead_amount) : undefined,
    loadedCost: opts.internalMode ? toNumber(item.loaded_cost_amount) : undefined,
    marginPct: opts.internalMode ? toNumber(item.margin_percentage_snapshot) : undefined,
    deliveryLabel: deliveryDays > 0 ? `${deliveryDays} day(s)` : '',
  };
}

function mapRentalItem(
  item: QuotationItem,
  opts?: { compactCustomerPrint?: boolean; technicalMode?: boolean }
): QuotationPrintItemBase {
  const compact = Boolean(opts?.compactCustomerPrint);
  const technical = Boolean(opts?.technicalMode);
  const description = String(item.item_description || '');
  const showSpecs =
    !compact && item.specifications && !isRentalSpecificationsRedundant(item);
  const breakdown = formatRentalLineCommercialBreakdown(item);
  const duration = toNumber(item.rental_duration);
  return {
    id: item.id,
    lineNumber: item.line_number,
    kind: 'rental',
    itemCode: item.item_code?.trim() || '',
    revision: item.revision?.trim() || '',
    description,
    descriptionChunks: splitDescriptionChunks(description),
    // Compact customer print: Description = item text only (no qty / period / dates).
    specifications: showSpecs ? String(item.specifications).trim() : '',
    notes: String(item.notes || '').trim(),
    quantityLabel: formatAmount(item.quantity).replace(/\.00$/, ''),
    unitPrice: technical ? 0 : toNumber(item.unit_price),
    netAmount: technical ? 0 : resolveQuotationLineNetAmount(item),
    uom: item.uom || 'PCS',
    period: String(item.rental_period_type || 'Daily').trim(),
    durationLabel: duration > 0 ? String(duration) : '—',
    datesText: compact ? '' : formatRentalDatesOnly(item),
    rate: technical ? 0 : rentalLineRate(item),
    rentalCharges: technical ? '' : formatRentalCharges(item) || '',
    rentalBreakdownLines:
      !technical && !compact && rentalPrintNeedsBreakdown(item) ? breakdown.summaryLines : [],
  };
}

export function buildQuotationPrintModel(input: BuildQuotationPrintModelInput): QuotationPrintModel {
  const {
    quotation,
    printMode = 'customer',
    customerOverheadDisplay = 'none',
    revenueServiceTypeLabel = '',
    revenueServiceType = null,
    approvalSigners = [],
    companyInfo = {},
    coverLetter,
  } = input;

  const uniqueItems = Array.from(
    new Map((quotation.items || []).map((item) => [item.id, item])).values()
  ).sort((a, b) => a.line_number - b.line_number);

  const printContext = resolveQuotationPrintContext(quotation, revenueServiceType);
  const customIntro = richTextToPlain(
    (printContext.layout === 'rental_only' ? coverLetter?.introRental : coverLetter?.intro) || ''
  );
  const introGreeting = richTextToPlainLine(coverLetter?.salutation || '') || DEFAULT_INTRO_GREETING;
  const introText = customIntro || printContext.introText;
  const rentalCompactCustomerPrint = printMode === 'customer_rental_compact';
  const technicalMode = printMode === 'technical';
  const customerFacing = printMode === 'customer' || rentalCompactCustomerPrint;
  const internalMode = printMode === 'internal' || printMode === 'internal_breakdown';
  const showOverheadBreakdown = printMode === 'internal_breakdown';
  const showCustomerOverheadSummary =
    customerFacing && !technicalMode && customerOverheadDisplay === 'summary';
  const showCustomerOverheadBreakdown =
    customerFacing && !technicalMode && customerOverheadDisplay === 'breakdown';

  const lineSubtotal = uniqueItems.reduce((s, i) => s + resolveQuotationLineNetAmount(i), 0);
  const subtotal = lineSubtotal > 0 ? lineSubtotal : toNumber(quotation.subtotal);
  const discountPct = toNumber(quotation.discount_percentage);
  const storedDiscount = toNumber(quotation.discount_amount);
  const discount =
    storedDiscount > 0 ? storedDiscount : discountPct > 0 ? (subtotal * discountPct) / 100 : 0;
  const taxable = subtotal - discount;
  const vatPct = toNumber(quotation.vat_percentage);
  let vat = 0;
  if (quotation.vat_applicable) {
    const lineVat = uniqueItems.reduce((s, i) => s + resolveQuotationLineVatAmount(i), 0);
    if (lineVat > 0.005) {
      vat = lineVat;
    } else if (vatPct > 0) {
      vat = (taxable * vatPct) / 100;
    } else {
      vat = toNumber(quotation.vat_amount);
    }
  }
  const computedGrand = taxable + vat;
  const storedGrand = toNumber(quotation.grand_total);
  const grand = lineSubtotal > 0 ? computedGrand : storedGrand > 0 ? storedGrand : computedGrand;
  const currency = quotation.currency || 'AED';

  const boqRef = quotation.boq_summary?.boq_number?.trim();
  const estRef = quotation.estimation_summary?.estimation_number?.trim();
  const treRef = quotation.trading_rental_estimation_summary?.estimation_number?.trim();
  const sourceEstimationRef = treRef || estRef || boqRef || '';
  const projectNumber =
    (quotation.project_number || quotation.project_summary?.project_number || '').trim();
  const projectNameOnly = (quotation.project_name || quotation.project_summary?.project_name || '').trim();
  const projectDisplay =
    projectNumber && projectNameOnly
      ? `${projectNumber} - ${projectNameOnly}`
      : projectNumber || projectNameOnly;
  const subject =
    (quotation.quotation_name || '').trim() ||
    projectDisplay ||
    (sourceEstimationRef ? `Quotation for ${sourceEstimationRef}` : '');

  const deliveryText = richTextToPlainLine(quotation.delivery_terms || '');
  const paymentText = richTextToPlainLine(quotation.payment_terms || '');
  const deliveryDaysMax = uniqueItems.reduce((m, i) => Math.max(m, toNumber(i.delivery_days)), 0);
  const deliveryDisplay =
    deliveryText ||
    (deliveryDaysMax > 0 ? `Within ${deliveryDaysMax} day(s) from order (as per line)` : '');
  const advancePct = toNumber(quotation.advance_payment_percentage);
  const paymentDisplay = [paymentText, advancePct > 0 ? `Advance: ${advancePct}%` : '']
    .filter(Boolean)
    .join(' | ');
  const termsAndConditions = (quotation.terms_and_conditions || '').trim();
  const notes = (quotation.notes || '').trim();
  const specialInstructions = (quotation.special_instructions || '').trim();

  const items: QuotationPrintItemBase[] = uniqueItems.map((item) =>
    isRentalQuotationItem(item) && printContext.layout !== 'trading_only'
      ? mapRentalItem(item, {
          compactCustomerPrint: rentalCompactCustomerPrint,
          technicalMode,
        })
      : mapTradingItem(item, { internalMode, technicalMode })
  );

  // Manufacturing forces trading layout; keep manufacturing columns flag.
  if (printContext.documentKind === 'manufacturing') {
    for (let i = 0; i < items.length; i++) {
      items[i] = { ...items[i], kind: 'trading' };
    }
  }

  const overheadLines: Array<{ label: string; amount: number }> = [];
  if (showOverheadBreakdown || showCustomerOverheadBreakdown) {
    for (const oh of quotation.overheads || []) {
      overheadLines.push({
        label: oh.description || oh.overhead_type || 'Overhead',
        amount: toNumber(oh.amount),
      });
    }
  }

  let customerOverheadSummary: string | undefined;
  if (showCustomerOverheadSummary && quotation.profitability_snapshot?.total_overhead != null) {
    customerOverheadSummary = `Overheads: ${formatAmount(quotation.profitability_snapshot.total_overhead)} ${currency}`;
  }

  const technicalIntro =
    'Technical copy for internal operations — quantities, specifications, and delivery only. No pricing.';

  return {
    title: printContext.title,
    subtitle: technicalMode
      ? 'Technical / Operations (no pricing)'
      : printContext.layout === 'mixed'
        ? 'Trading & rental quotation'
        : undefined,
    layout: printContext.layout,
    documentKind: printContext.documentKind,
    manufacturingColumns: printContext.documentKind === 'manufacturing',
    internalMode,
    technicalMode,
    introGreeting: technicalMode ? 'Internal operations,' : introGreeting,
    introText: technicalMode ? technicalIntro : introText,
    quotationNumber: quotation.quotation_number,
    revisionLabel: displayQuotationRevisionLabel(quotation.revision_number),
    revisionNumber: Number(quotation.revision_number) || 1,
    quotationDate: formatDate(quotation.quotation_date),
    customerName: quotation.customer_name || '',
    customerAddress: (quotation.customer_address || '').trim(),
    attn: (quotation.customer_contact || '').trim(),
    projectName: projectDisplay,
    subject,
    trn: companyInfo.taxNumber?.replace(/^TRN:\s*/i, '') || companyInfo.vatNumber || '',
    inquiryReference: (quotation.inquiry_reference || '').trim(),
    sapNumber: (quotation.sap_number || '').trim(),
    opportunityNo: quotation.opportunity_summary?.opportunity_number?.trim() || '',
    estimationNo: treRef || estRef || boqRef || '',
    manufacturer: revenueServiceTypeLabel || '',
    items,
    totals: {
      subtotal,
      discount,
      discountPct,
      taxable,
      vat,
      vatPct,
      vatApplicable: Boolean(quotation.vat_applicable),
      grand,
      currency,
      amountInWords: formatAmountInWords(grand, currency),
    },
    terms: technicalMode
      ? [
          { key: 'Delivery', value: deliveryDisplay || '—' },
          ...(quotation.validity_days
            ? [{ key: 'Validity', value: `${quotation.validity_days} Days` }]
            : []),
        ]
      : [
          { key: 'Delivery', value: deliveryDisplay || '—' },
          { key: 'Validity', value: quotation.validity_days ? `${quotation.validity_days} Days` : '—' },
          { key: 'Valid Until', value: quotation.valid_until ? formatDate(quotation.valid_until) : '—' },
          { key: 'Payment', value: paymentDisplay || '—' },
        ],
    extraTerms: technicalMode ? '' : termsAndConditions,
    notes,
    specialInstructions,
    termsAndConditionsChunks: technicalMode
      ? []
      : buildQuotationTermsAndConditionsChunks(termsAndConditions),
    noteChunks: buildQuotationNoteChunks(notes, specialInstructions),
    signers: approvalSigners || [],
    showOverheadBreakdown,
    overheadLines,
    customerOverheadSummary,
    rentalCompactCustomerPrint,
  };
}
